import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "terakki_theme_mode";

const read = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // Tarayıcı depolaması kapalıysa varsayılan kullanılır.
  }
  // Mobil uygulama gibi varsayılan koyu.
  return "dark";
};

const ThemeModeContext = createContext({
  mode: "dark",
  setMode: () => {},
  toggle: () => {},
});

/**
 * Uygulamanın koyu/açık tema seçimi (mobildeki MainThemeContext karşılığı).
 * Seçim tarayıcıda saklanır. Tema, uygulama kabı (AppShell) içinde uygulanır:
 * <html>'e "dark" sınıfı eklenir (Tailwind dark: çeşitleri) ve MUI teması
 * koyu kipe geçer. Giriş ve tanıtım sayfaları açık kalır.
 */
export function ThemeModeProvider({ children }) {
  const [mode, setModeState] = useState(read);

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Saklanamazsa yalnız bu oturumda geçerli olur.
    }
  }, []);

  const toggle = useCallback(() => setMode(mode === "dark" ? "light" : "dark"), [mode, setMode]);

  // Başka sekmede değişirse bu sekme de uysun.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && (e.newValue === "light" || e.newValue === "dark")) {
        setModeState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle]);
  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export const useThemeMode = () => useContext(ThemeModeContext);
